import { getLocalStorageItem, removeLocalStorageItem, setLocalStorageItem } from "../../../logger/src/LocalStorage";
import { v4 as uuid } from 'uuid';
import { formatError, Logger } from "../../../logger/src/Logger";
import { app as msTeamsApp } from '@microsoft/teams-js';
import { whenDeviceRegistered, whenWebexReady } from "./WebexInstanceService";
import { waitForCallingBackEndReady } from "../utils/callingUtils";
import {
    SubscriptionEventResponse,
    DATA_PIPE_LINE_EVENTS,
    IntegrationErrorResponse,
    IntegrationPhoneServicesResponse,
    IntegrationSettingsResponse,
    DISPATCH_STORAGE_EVENTS,
    IntegrationE911Response
} from '../../../adapter-interfaces/src';
import { EventType, eventEmitter } from "../utils/eventEmitter";
import { WebexTokenEnum, msCrossLaunchToken } from "../../../../ms-teams-sdk/service/MsTeamsAuthenticationService";

let makeIntegrationSubscriptionRegister = true;
let makeIntegrationErrorRegister = true;
let makeIntegrationPhoneServicesRegister = true;
let makeIntegrationSettingsRegister = true;
let makeIntegrationE911Register = true;

// Shared promises for event listeners - prevents multiple listeners and ensures all callers wait for the same event
let phoneServicesPromise: Promise<IntegrationPhoneServicesResponse> | null = null;
let dataPipelineStartPromise: Promise<void> | null = null;
let phoneServicesRetryCount = 0;
let pendingIntegrationSettingsResolvers: Array<(d: IntegrationSettingsResponse) => void> = [];
const LAST_KNOWN_GOOD_PHONE_SERVICES_DATA = 'storage_phone_services_data_last_known_good';

// Latest payload pushed by the persistent Mercury listener. Used to:
//   1. Resolve subsequent getPhoneServicesData() callers immediately (no extra wait)
//   2. Allow side effects (banner refresh, persistence) to keep firing on every
//      Mercury event after the first one (status: disconnected -> connecting -> connected).
let lastPhoneServicesData: IntegrationPhoneServicesResponse | null = null;
// One-shot resolvers waiting on the FIRST integration.phoneservices event. The
// persistent listener flushes this list whenever an event arrives.
let pendingPhoneServicesResolvers: Array<(d: IntegrationPhoneServicesResponse) => void> = [];
let phoneServicesTimeoutId: ReturnType<typeof setTimeout> | null = null;
let phoneServicesRetryTimeoutId: ReturnType<typeof setTimeout> | null = null;
const MOCK_INTEGRATION_SETTINGS_DATA = {
    "id": "e21f0d78-0448-4255-a978-ea7c1f7f9c70",
    "data": {
        "eventType": "integration.settings",
        "id": "308bba63-2f56-4db8-8f95-c5630bed8a00",
        "payload": {
            "scope": "full",
            "settings": {
                "calling.callingdock.enabled": "true"
            }
        },
        "subscriptionId": "ba19d486-6e99-41ef-a739-684a5aeb8ae3"
    },
    "timestamp": 1733977789275,
    "trackingId": "CLIENT_69972b13-8253-4a2b-859a-f1fb5cbf6a14",
    "alertType": "none",
    "headers": {},
    "sequenceNumber": 4,
    "filterMessage": false,
    "wsWriteTimestamp": 1733977789275
}
const MOCK_E911_DATA = {
    "id": "42714560-bd44-41c9-99d1-969fe3bfd62b",
    "data": {
        "eventType": "integration.e911",
        "id": "f1ca2e7e-f3bf-4e0b-9ae4-0fa9b15997cb",
        "payload": {
            "emergencyLocationValidated": true,
            "featureEnabled": true
        },
        "subscriptionId": "b1519880-43db-4de6-9b7c-359c63489775"
    },
    "timestamp": 1734667099559,
    "trackingId": "CLIENT_06adfcce-df28-45bc-be09-451cfccf25f8",
    "alertType": "none",
    "headers": {},
    "sequenceNumber": 5,
    "filterMessage": false,
    "wsWriteTimestamp": 1734667099559
}

const dispatchScopedEvent = (key: string) => {
    const event = new CustomEvent(`localstorage:${key}`);
    window.dispatchEvent(event);
};

const delay = (ms: number, shouldAbort?: () => boolean) =>
    new Promise<void>((resolve) => {
        if (shouldAbort?.()) {
            resolve();
            return;
        }
        const timer = setTimeout(() => resolve(), ms);
        if (shouldAbort) {
            const check = () => {
                if (shouldAbort()) {
                    clearTimeout(timer);
                    resolve();
                }
            };
            // Quick check once right after scheduling
            setTimeout(check, 0);
        }
    });

/**
 * Get cached phone services data as emergency fallback ONLY.
 * This is used when:
 * 1. Mercury event timeout occurs AND
 * 2. No fresh data available in current session
 *
 * Returns last-known-good data from previous session to prevent UI breakage.
 *
 * NOTE: This is NOT used for normal first-load (always fetch fresh).
 * Normal first-load always waits for Mercury event with retry logic.
 */
function getCachedPhoneServicesDataForFallbackOnly(): IntegrationPhoneServicesResponse | null {
    const lastKnownGood = getLocalStorageItem<IntegrationPhoneServicesResponse>(
        LAST_KNOWN_GOOD_PHONE_SERVICES_DATA
    );
    if (lastKnownGood?.data?.payload?.lines?.length) {
        Logger.info(
            `[LineSelection] Using last-known-good Phone Services fallback (timeout) ` +
            `(${lastKnownGood.data.payload.lines.length} lines from previous session)`
        );
        return lastKnownGood;
    }
    return null;
}

function persistPhoneServicesData(sessionData: IntegrationPhoneServicesResponse): void {
    setLocalStorageItem(DISPATCH_STORAGE_EVENTS.STORAGE_PHONE_SERVICES_DATA, sessionData);
    if (sessionData?.data?.payload?.lines?.length) {
        setLocalStorageItem(LAST_KNOWN_GOOD_PHONE_SERVICES_DATA, sessionData);
    }
}

export async function waitForCrossLaunchToken(timeoutMs = 120000, pollMs = 1000): Promise<string> {
    const start = Date.now();
    while (true) {
        const token = getLocalStorageItem<string>(WebexTokenEnum.WEBEX_CROSS_LAUNCH_TOKEN);
        if (token) {
            return token;
        }
        if (Date.now() - start > timeoutMs) {
            throw new Error('waitForCrossLaunchToken: timeout waiting for webex cross-launch token');
        }
        await delay(pollMs);
    }
}

type DataPipelineDeps = {
    waitForWebex: () => Promise<any>;
    waitForDevice: () => Promise<void>;
    waitForCallingBackend: () => Promise<any>;
    waitForCrossLaunch: () => Promise<any>;
    subscribe: () => Promise<any>;
    shouldAbort?: () => boolean;
};

const defaultDataPipelineDeps: DataPipelineDeps = {
    waitForWebex: () => whenWebexReady(),
    waitForDevice: () => whenDeviceRegistered(),
    waitForCallingBackend: () => waitForCallingBackEndReady(),
    waitForCrossLaunch: () => waitForCrossLaunchToken(),
    // Register the settings consumer before the desktop subscription publishes
    // its initial full snapshots. Otherwise a fast integration.settings response
    // can be received by Mercury before the application handler exists.
    subscribe: () => {
        registerIntegrationSettingsListener();
        const mercury = window?.webex?.internal?.mercury;
        if (!mercury || typeof mercury.on !== 'function') {
            throw new Error('Mercury not ready while starting data pipeline subscription');
        }
        return getDataPipelineSubscription();
    },
    shouldAbort: () => false,
};

export async function startDataPipelineWhenReady(
    options?: { maxAttempts?: number; baseBackoffMs?: number },
    overrides?: Partial<DataPipelineDeps>
): Promise<void> {
    const deps: DataPipelineDeps = { ...defaultDataPipelineDeps, ...(overrides || {}) };
    const maxAttempts = options?.maxAttempts ?? 5;
    const baseBackoffMs = options?.baseBackoffMs ?? 1000;

    for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
        if (deps.shouldAbort?.()) {
            return;
        }
        try {
            await deps.waitForWebex();
            await deps.waitForDevice();
            await deps.waitForCallingBackend();
            await deps.waitForCrossLaunch();
            await deps.subscribe();
            Logger.info(`Data pipeline subscription started (attempt ${attempt})`);
            return;
        } catch (error) {
            Logger.error(
                `startDataPipelineWhenReady attempt ${attempt} failed: '${formatError(error)}'`
            );
            if (attempt === maxAttempts || deps.shouldAbort?.()) {
                throw error;
            }
            const backoff = baseBackoffMs * attempt;
            await delay(backoff, deps.shouldAbort);
        }
    }
}

export function ensureDataPipelineStarted(
    options?: { maxAttempts?: number; baseBackoffMs?: number },
    overrides?: Partial<DataPipelineDeps>
): Promise<void> {
    if (dataPipelineStartPromise) {
        Logger.debug('Data pipeline startup already in progress or completed; reusing promise');
        return dataPipelineStartPromise;
    }

    Logger.info('Ensuring data pipeline startup before waiting for integration events');
    dataPipelineStartPromise = startDataPipelineWhenReady(options, overrides)
        .catch((error) => {
            dataPipelineStartPromise = null;
            throw error;
        });

    return dataPipelineStartPromise;
}

export async function openIntegrationLink(link: string, reason: string): Promise<void> {
    try {
        Logger.info(`Opening integration link via Teams SDK (${reason})`);
        await msTeamsApp.openLink(link);
    } catch (sdkError) {
        Logger.error(
            `Teams SDK openLink failed for ${reason}: '${formatError(sdkError)}'. Falling back to window.open().`
        );
        window.open(link);
    }
}

export const openWebexUrlForAckEvent = (eventId: string, featureStatus: string): void => {
    if (!eventId) {
      Logger.info(`Missing Ack Event Id for ${featureStatus}`);
      return;
    }
    Logger.info(`openWebexUrlForAckEvent(): Feature state: ${featureStatus} | Event ID: ${eventId}`);
  
    const webexUrl = `webex://integration?cmd=ack&eventId=${eventId}&source=MSTeams`;
    const callUrl = new URL(webexUrl);
    void openIntegrationLink(callUrl.href, `ack:${featureStatus}`);
  };

export const reNewSubscriptionOnTermination = (subscriptionId: string, requestId: string): void => {
    const webex_crossLaunchToken = getLocalStorageItem<string>("webex_crossLaunchToken");
    const deviceUrl = window.webex.internal.device.getId();
    // Proceed only if the token is available
    if (webex_crossLaunchToken) {
        Logger.info(`Sending new subscription when recieving subscriptin termination event`);
        // Retrieve the device ID using window.webex.internal.device.getId()
        Logger.info(`WebexPhoneService.initDataPipelineSubscription() deviceUrl: ${deviceUrl}`);

        // Construct the Webex cross-launch URL with the generated values
        const webexUrl = `webex://integration?cmd=subscribe&events=phoneservices,settings,e911&subscriptionId=${subscriptionId}&requestId=${requestId}&deviceUrl=${deviceUrl}&source=MSTeams&jws=${webex_crossLaunchToken}`;
        const callUrl = new URL(webexUrl);

        void openIntegrationLink(callUrl.href, 'renew-subscription');
    } else {
        Logger.info('Webex crossLaunchToken is not available while attempting a new subscription after receiving the subscription termination event')

    }

};
// Helper: Open cross-launch URL to trigger phone services subscription from Webex Desktop
async function openPhoneServicesSubscriptionUrl(): Promise<void> {
    try {
        const webex_crossLaunchToken = getLocalStorageItem<string>("webex_crossLaunchToken");
        let token = webex_crossLaunchToken;
        if (!token) {
            try {
                token = await msCrossLaunchToken();
                Logger.debug(`[LineSelection] Using MS cross-launch token for retry`);
            } catch (e) {
                Logger.debug(`[LineSelection] No cross-launch token for retry`);
            }
        }
        if (token) {
            const subscriptionId = uuid();
            const requestId = uuid();
            const deviceUrl = window.webex.internal.device.getId();
            const webexUrl = `webex://integration?cmd=subscribe&events=phoneservices,settings,e911&subscriptionId=${subscriptionId}&requestId=${requestId}&deviceUrl=${deviceUrl}&source=MSTeams&jws=${token}`;
            Logger.info(`[LineSelection] Opening cross-launch URL for phone services subscription (retry)`);
            await openIntegrationLink(webexUrl, "phone-services-subscription-retry");
        }
    } catch (err) {
        Logger.debug(`[LineSelection] Failed to open cross-launch on retry: ${formatError(err)}`);
    }
}


// Async function to initialize the data pipeline subscription
async function initDataPipelineSubscription() {
    try {
        const initialSubscriptionFTCheck = await window.webex.internal.feature.getFeature('developer', 'desktop-msteams-integration-data-channel-spark-506019');
        const devSubscriptionFTCHECK =  await window.webex.internal.feature.getFeature('developer', 'desktop-msteams-integration-data-channel-spark-506019-dev');
        const gaSubscriptionFTCheck =  await window.webex.internal.feature.getFeature('developer', 'desktop-msteams-integration-data-channel-spark-506019-ga');
        if (gaSubscriptionFTCheck || initialSubscriptionFTCheck || devSubscriptionFTCHECK) {
            Logger.info("Data pipeline feature toggle check is enabled")
            const webex_crossLaunchToken = getLocalStorageItem<string>("webex_crossLaunchToken");

            // Fall back to MS cross-launch token if webex token is not available
            // (same fallback pattern used in CrossLaunchService.ts for calls)
            let token = webex_crossLaunchToken;
            if (!token) {
                try {
                    token = await msCrossLaunchToken();
                    Logger.info(`WebexPhoneService.initDataPipelineSubscription(): using MS cross-launch token as fallback`);
                } catch (e) {
                    Logger.info(`WebexPhoneService.initDataPipelineSubscription(): no cross-launch token available`);
                }
            }

            // Proceed only if a token is available
            if (token) {
                Logger.info(`WebexPhoneService.initDataPipelineSubscription(): data pipeline cross launching`);
                // Generate UUIDs for subscriptionId and requestId
                const subscriptionId = uuid();
                const requestId = uuid();
                // Retrieve the device ID using window.webex.internal.device.getId()
                const deviceUrl = window.webex.internal.device.getId();
                Logger.info(`WebexPhoneService.initDataPipelineSubscription() deviceUrl: ${deviceUrl}`);

                // Construct the Webex cross-launch URL with the generated values
                const webexUrl = `webex://integration?cmd=subscribe&events=phoneservices,settings,e911&subscriptionId=${subscriptionId}&requestId=${requestId}&deviceUrl=${deviceUrl}&source=MSTeams&jws=${token}`;
                const callUrl = new URL(webexUrl);

                await openIntegrationLink(callUrl.href, 'init-subscription');
            }
        } else {
            Logger.info("Data pipeline feature toggle check is not enabled")
        }
    } catch (error) {
        Logger.error(
            `WebexPhoneService.initDataPipelineSubscription(): Error initializing data pipeline subscription:: '${formatError(
                error
            )}'.`
        );
        throw error;
    }
}


const MAX_SDK_POLL_ATTEMPTS = 60;

export async function getDataPipelineSubscription(attempt = 0): Promise<any> {
    if (window?.isWebexInitalized) {
        return initDataPipelineSubscription();
    } else if (attempt >= MAX_SDK_POLL_ATTEMPTS) {
        throw new Error('getDataPipelineSubscription: Webex SDK not initialized after max attempts');
    } else {
        await new Promise((f) => setTimeout(f, 1000));
        return getDataPipelineSubscription(attempt + 1);
    }
}

// ---------------------------------------------------------------------------
// Reconnect re-subscription
//
// After a Mercury websocket reconnect (sleep/wake, network blip, transport
// drop) the *server-side* integration subscription created before the drop can
// go stale. When that happens the socket comes back but `integration.settings`
// (and phoneservices / e911) partial pushes silently stop reaching the tab, so
// the Calling Dock toggle freezes on its last value even though the user keeps
// clicking it. This was reproduced on both UC Connector (CUCM) and Webex
// Calling backends and is deterministically tied to the reconnect event.
//
// The fix renews the subscription on reconnect so the server resumes delivering
// confirmations. We deliberately re-issue only the *subscribe* cross-launch:
// the Mercury `.on(...)` handlers persist across reconnects (they live on the
// JS event emitter, not the socket), so re-registering them would create
// duplicate handlers, duplicate localStorage writes, and duplicate `ack`
// cross-launches. Renewing the subscription alone is sufficient for the
// existing persistent handlers to resume updating the UI.
let mercuryReconnectHandlerRegistered = false;
let windowOnlineHandlerRegistered = false;
let lastResubscribeAt = 0;
// Throttle window: dedupes the near-simultaneous Mercury `online` + browser
// `online` events for a single reconnect, and (via seeding on registration)
// prevents the initial startup connect from triggering a redundant subscribe.
const RESUBSCRIBE_MIN_INTERVAL_MS = 5000;

type ReconnectResubscribeOverrides = {
    resubscribe?: () => Promise<unknown>;
    now?: () => number;
};

/**
 * Renew the integration data-pipeline subscription after a reconnect, throttled
 * so a burst of reconnect signals can't spam the protocol handler with repeated
 * subscribe cross-launches. Safe to call on every reconnect signal.
 */
export async function resubscribeDataPipelineOnReconnect(
    reason: string,
    overrides?: ReconnectResubscribeOverrides
): Promise<void> {
    const now = (overrides?.now ?? Date.now)();
    if (now - lastResubscribeAt < RESUBSCRIBE_MIN_INTERVAL_MS) {
        Logger.debug(
            `resubscribeDataPipelineOnReconnect: skipping (${reason}); throttled within ${RESUBSCRIBE_MIN_INTERVAL_MS}ms of last attempt`
        );
        return;
    }
    lastResubscribeAt = now;
    try {
        Logger.info(`Renewing integration data pipeline subscription after reconnect (${reason})`);
        await (overrides?.resubscribe ?? getDataPipelineSubscription)();
    } catch (error) {
        Logger.error(
            `resubscribeDataPipelineOnReconnect: failed to renew subscription (${reason}): '${formatError(error)}'`
        );
    }
}

type ReconnectHandlerOverrides = ReconnectResubscribeOverrides & {
    mercury?: { on?: (event: string, cb: (...args: unknown[]) => void) => void } | null;
    addWindowListener?: (event: string, cb: () => void) => void;
};

/**
 * Wire up reconnect-driven re-subscription for the integration data pipeline.
 *
 * Idempotent: subsequent calls are no-ops once the handlers are attached, so it
 * is safe against React re-mounts / strict-mode double effects. Call this once
 * the initial subscription has been established (Mercury is up).
 */
export function registerDataPipelineReconnectHandler(
    overrides?: ReconnectHandlerOverrides
): void {
    // Seed the throttle window once so a Mercury `online` (or browser `online`)
    // that fires right after the startup subscription is not misread as a
    // reconnect. Genuine reconnects happen well beyond this window.
    if (lastResubscribeAt === 0) {
        lastResubscribeAt = (overrides?.now ?? Date.now)();
    }

    const resubscribeOverrides: ReconnectResubscribeOverrides = {
        resubscribe: overrides?.resubscribe,
        now: overrides?.now,
    };

    // Mercury transport reconnect — the most accurate signal. Mercury emits
    // `online` on connect and on every reconnect (see @webex/internal-plugin-
    // mercury `_emit('online')`). By the time this runs the pipeline is already
    // subscribed and Mercury is connected, so every `online` we observe here is
    // a reconnect (the throttle covers any startup-adjacent edge case).
    const mercury = overrides?.mercury ?? (window as any)?.webex?.internal?.mercury;
    if (!mercuryReconnectHandlerRegistered && mercury && typeof mercury.on === 'function') {
        mercury.on('online', () => {
            void resubscribeDataPipelineOnReconnect('mercury:online', resubscribeOverrides);
        });
        mercuryReconnectHandlerRegistered = true;
        Logger.debug('Registered Mercury reconnect handler for data pipeline re-subscription');
    } else if (!mercuryReconnectHandlerRegistered) {
        Logger.info('Mercury not ready while registering reconnect handler; relying on window online fallback');
    }

    // Browser network reconnection fallback — covers sleep/wake and cases where
    // Mercury does not surface an explicit online event. Mirrors the existing
    // `online` refresh pattern in useLineSelection. Throttling in
    // resubscribeDataPipelineOnReconnect prevents a duplicate subscribe when
    // both this and the Mercury handler fire for the same reconnect.
    const addWindowListener =
        overrides?.addWindowListener ??
        (typeof window?.addEventListener === 'function'
            ? (event: string, cb: () => void) => window.addEventListener(event, cb)
            : undefined);
    if (!windowOnlineHandlerRegistered && addWindowListener) {
        addWindowListener('online', () => {
            void resubscribeDataPipelineOnReconnect('window:online', resubscribeOverrides);
        });
        windowOnlineHandlerRegistered = true;
        Logger.debug('Registered window online fallback handler for data pipeline re-subscription');
    }
}

/**
 * Test-only reset of the reconnect handler module state. Not used in
 * production code paths.
 */
export function __resetDataPipelineReconnectStateForTests(): void {
    mercuryReconnectHandlerRegistered = false;
    windowOnlineHandlerRegistered = false;
    lastResubscribeAt = 0;
}

async function listenSubscriptionStatus(): Promise<SubscriptionEventResponse> {
    return new Promise<SubscriptionEventResponse>((resolve) => {
        // Check if data already exists before setting up listener
        const existingData = getLocalStorageItem<SubscriptionEventResponse>(
            DISPATCH_STORAGE_EVENTS.STORAGE_SUBSCRIPTION_DATA
        );
        if (existingData?.data?.eventType === DATA_PIPE_LINE_EVENTS.INTEGRATION_SUBSCRIPTION) {
            Logger.info(
                `[LineSelection] Subscription data already in localStorage. Returning cached data.`
            );
            resolve(existingData);
            return;
        }

        if (makeIntegrationSubscriptionRegister) {
            const eventHandler = (sessionData: SubscriptionEventResponse) => {
                Logger.info(
                    `User subscribed to the event '${sessionData.data.eventType}'`
                );
                eventEmitter.dispatch(EventType.INTEGRATION_SUBSCRIPTION_EVENT_TYPE, "integrationSubscription");
                setLocalStorageItem(DISPATCH_STORAGE_EVENTS.STORAGE_SUBSCRIPTION_DATA, sessionData);
                dispatchScopedEvent("subscription");
                resolve(sessionData); // Resolve the promise with the event payload
            };

            window.webex.internal.mercury.on(
                DATA_PIPE_LINE_EVENTS.INTEGRATION_SUBSCRIPTION,
                eventHandler
            );

            // window.webex.internal.mercury.on(DATA_PIPE_LINE_EVENTS.INTEGRATION_PHONE_SERVICES, (event: any) => {
            //     console.log('Phone services event from webex', event)
            //     eventEmitter.dispatch(EventType.INTEGRATION_SUBSCRIPTION_EVENT_TYPE, "integrationSubscription");
            //   });

            //   window.webex.internal.mercury.on(DATA_PIPE_LINE_EVENTS.INTEGRATION_SETTINGS, (event: any) => {
            //     console.log('Phone settings event from webex', event)
            //     eventEmitter.dispatch(EventType.INTEGRATION_SUBSCRIPTION_EVENT_TYPE, "integrationSubscription");
            //   });

            //   window.webex.internal.mercury.on(DATA_PIPE_LINE_EVENTS.INTEGRATION_E911, (event: any) => {
            //     console.log('E911 event from webex', event)
            //     eventEmitter.dispatch(EventType.INTEGRATION_SUBSCRIPTION_EVENT_TYPE, "integrationSubscription");
            //   });

            //   window.webex.internal.mercury.on(DATA_PIPE_LINE_EVENTS.INTEGRATION_ERROR, (event: any) => {
            //     console.log('Error event from webex', event)
            //     eventEmitter.dispatch(EventType.INTEGRATION_SUBSCRIPTION_EVENT_TYPE, "integrationSubscription");
            //   });

              makeIntegrationSubscriptionRegister = false;
        }
    });
}

export async function getSubscriptionStatus(attempt = 0): Promise<SubscriptionEventResponse> {
    if (window?.isWebexInitalized) {
        return listenSubscriptionStatus();
    } else if (attempt >= MAX_SDK_POLL_ATTEMPTS) {
        throw new Error('getSubscriptionStatus: Webex SDK not initialized after max attempts');
    } else {
        await new Promise((f) => setTimeout(f, 1000));
        return getSubscriptionStatus(attempt + 1);
    }
}

/**
 * Build an empty fallback payload so first-load callers don't hang forever when
 * Mercury never delivers an event in the configured window.
 */
function buildEmptyPhoneServicesFallback(): IntegrationPhoneServicesResponse {
    return {
        id: '',
        data: {
            eventType: DATA_PIPE_LINE_EVENTS.INTEGRATION_PHONE_SERVICES,
            id: '',
            payload: { lines: [] },
            subscriptionId: ''
        },
        timestamp: Date.now(),
        trackingId: '',
        alertType: 'none',
        headers: {},
        sequenceNumber: 0,
        filterMessage: false,
        wsWriteTimestamp: Date.now()
    } as any;
}

/**
 * Flush all one-shot resolvers waiting on the first integration.phoneservices
 * event. Safe to call when the list is empty.
 */
function flushPendingPhoneServicesResolvers(sessionData: IntegrationPhoneServicesResponse): void {
    if (phoneServicesTimeoutId) {
        clearTimeout(phoneServicesTimeoutId);
        phoneServicesTimeoutId = null;
    }
    if (phoneServicesRetryTimeoutId) {
        clearTimeout(phoneServicesRetryTimeoutId);
        phoneServicesRetryTimeoutId = null;
    }
    if (pendingPhoneServicesResolvers.length === 0) {
        phoneServicesPromise = null;
        return;
    }
    const resolvers = pendingPhoneServicesResolvers;
    pendingPhoneServicesResolvers = [];
    phoneServicesPromise = null;
    resolvers.forEach((resolveFn) => {
        try {
            resolveFn(sessionData);
        } catch (err) {
            Logger.error(
                `[LineSelection] Pending phone services resolver threw: '${formatError(err)}'`
            );
        }
    });
}

/**
 * Persistent Mercury event handler for `event:integration.phoneservices`.
 *
 * Registered exactly once per page. Runs on EVERY Mercury event so the UI banner
 * (PhoneServicesAlert) can react to status transitions (connected ⇄ disconnected
 * ⇄ connecting). Previously this work lived inside a one-shot promise closure
 * that short-circuited after the first event via a `resolved` flag, which
 * caused disconnect/reconnect banners to never appear.
 */
function persistentPhoneServicesHandler(sessionData: IntegrationPhoneServicesResponse): void {
    Logger.info(
        `[LineSelection] Mercury event received: Phone services event ` +
        `'${sessionData?.data?.eventType}', status='${sessionData?.data?.payload?.status ?? ''}', ` +
        `${sessionData?.data?.payload?.lines?.length || 0} lines`
    );
    lastPhoneServicesData = sessionData;
    eventEmitter.dispatch(EventType.INTEGRATION_PHONE_SERVICES_EVENT_TYPE, "integrationPhoneServices");
    persistPhoneServicesData(sessionData);
    dispatchScopedEvent("phoneServices");
    flushPendingPhoneServicesResolvers(sessionData);
}

/**
 * Resolve the best cached payload for callers that need lines (LineSelector,
 * PhoneServicesLineService). Prefer the latest Mercury payload, but if it has
 * no lines (e.g. a transient disconnect frame from Webex Desktop) fall back to
 * the last-known-good payload so the LineSelector dropdown doesn't blank out
 * during status flips. Mirrors the protection that `persistPhoneServicesData`
 * already applies to `LAST_KNOWN_GOOD_PHONE_SERVICES_DATA`.
 */
function pickBestCachedPhoneServicesData(): IntegrationPhoneServicesResponse | null {
    if (lastPhoneServicesData?.data?.payload?.lines?.length) {
        return lastPhoneServicesData;
    }
    const lastKnownGood = getCachedPhoneServicesDataForFallbackOnly();
    if (lastKnownGood?.data?.payload?.lines?.length) {
        Logger.debug(
            `[LineSelection] Latest Phone Services payload has no lines (status='${lastPhoneServicesData?.data?.payload?.status ?? ''}'); ` +
            `serving last-known-good payload (${lastKnownGood.data.payload.lines.length} lines) to preserve LineSelector state`
        );
        return lastKnownGood;
    }
    return lastPhoneServicesData;
}

async function listenPhoneServicesStatus(): Promise<IntegrationPhoneServicesResponse> {
    // Fast path: if the persistent listener has already received at least one
    // Mercury event, resolve immediately with the best available cached
    // payload. New callers (e.g. PhoneServicesLineService.getLines() after a
    // navigation, or useLineSelection's Layer-3 re-fetch on
    // localstorage:phoneServices) no longer need to wait the full 10s timeout
    // window. The helper biases toward payloads that have lines so a transient
    // disconnect frame can't briefly empty the LineSelector.
    if (lastPhoneServicesData) {
        const cached = pickBestCachedPhoneServicesData();
        if (cached) {
            Logger.debug(
                `[LineSelection] Returning cached Phone Services data (status='${cached?.data?.payload?.status ?? ''}', ` +
                `${cached?.data?.payload?.lines?.length || 0} lines)`
            );
            return cached;
        }
    }

    // If a one-shot first-load promise is already in flight, reuse it so all
    // callers wait on the same Mercury event.
    if (phoneServicesPromise) {
        Logger.info(`[LineSelection] Phone Services listener already in progress, reusing existing promise`);
        return phoneServicesPromise;
    }

    phoneServicesPromise = new Promise<IntegrationPhoneServicesResponse>((resolve) => {
        Logger.info(
            `[LineSelection] listenPhoneServicesStatus: Registering Mercury listener to fetch Phone Services data`
        );

        pendingPhoneServicesResolvers.push(resolve);

        // First-load timeout: only one timeout runs at a time (subsequent callers
        // join the resolver registry above and share this same deadline).
        if (!phoneServicesTimeoutId) {
            phoneServicesTimeoutId = setTimeout(() => {
                phoneServicesTimeoutId = null;
                if (lastPhoneServicesData) {
                    // A Mercury event arrived between the timeout fire and now -
                    // flush with the real data instead of fallback.
                    flushPendingPhoneServicesResolvers(lastPhoneServicesData);
                    return;
                }
                Logger.error(
                    `[LineSelection] Mercury INTEGRATION_PHONE_SERVICES event timeout after 10s - no data received`
                );
                const fallbackData = getCachedPhoneServicesDataForFallbackOnly();
                if (fallbackData?.data?.payload?.lines?.length) {
                    Logger.info(
                        `[LineSelection] Timeout - using last-known-good fallback ` +
                        `(${fallbackData.data.payload.lines.length} lines from previous session)`
                    );
                    flushPendingPhoneServicesResolvers(fallbackData);
                } else {
                    flushPendingPhoneServicesResolvers(buildEmptyPhoneServicesFallback());
                }

                // Nudge Webex Desktop to republish the event. The persistent
                // listener stays registered, so any subsequent Mercury delivery
                // updates lastPhoneServicesData and refreshes the UI.
                if (phoneServicesRetryCount < 3) {
                    phoneServicesRetryCount++;
                    Logger.info(
                        `[LineSelection] Scheduling auto-retry ${phoneServicesRetryCount}/3 for Phone Services in 5s`
                    );
                    phoneServicesRetryTimeoutId = setTimeout(() => {
                        phoneServicesRetryTimeoutId = null;
                        Logger.info(
                            `[LineSelection] Auto-retry ${phoneServicesRetryCount} - re-triggering cross-launch`
                        );
                        void openPhoneServicesSubscriptionUrl();
                    }, 5000);
                }
            }, 10000);
        }

        // Register the persistent Mercury listener exactly once per page. The
        // handler runs on every subsequent event, not just the first one.
        if (makeIntegrationPhoneServicesRegister && window?.webex?.internal?.mercury?.on) {
            window.webex.internal.mercury.on(
                DATA_PIPE_LINE_EVENTS.INTEGRATION_PHONE_SERVICES,
                persistentPhoneServicesHandler
            );
            Logger.debug(
                `[LineSelection] Mercury listener registered for event: ${DATA_PIPE_LINE_EVENTS.INTEGRATION_PHONE_SERVICES}`
            );
            makeIntegrationPhoneServicesRegister = false;
        } else if (!window?.webex?.internal?.mercury?.on) {
            Logger.info('[LineSelection] Mercury not ready yet while registering phone services listener');
        }
    });

    return phoneServicesPromise;
}

export async function getPhoneServicesData(attempt = 0): Promise<IntegrationPhoneServicesResponse> {
    if (window?.isWebexInitalized) {
        // ALWAYS fetch fresh data on app load (no cache on first check).
        // This ensures data changes (new lines added, etc.) are always detected.
        // Only cache is used as emergency fallback on timeout.

        // Register the Mercury listener BEFORE starting the protocol-handler subscription.
        // In the embedded Teams browser, the Webex desktop app may publish the
        // integration.phoneservices event immediately after the subscribe launch.
        // If we subscribe first and only then register the listener, the first event
        // can be missed and the UI will not populate until a later Webex launch.
        const phoneServicesListenerPromise = listenPhoneServicesStatus();
        await ensureDataPipelineStarted();
        return phoneServicesListenerPromise;
    } else if (attempt >= MAX_SDK_POLL_ATTEMPTS) {
        throw new Error('getPhoneServicesData: Webex SDK not initialized after max attempts');
    } else {
        await new Promise((f) => setTimeout(f, 1000));
        return getPhoneServicesData(attempt + 1);
    }
}

//Integration.settings service

function flushPendingIntegrationSettingsResolvers(sessionData: IntegrationSettingsResponse): void {
    const resolvers = pendingIntegrationSettingsResolvers;
    pendingIntegrationSettingsResolvers = [];
    resolvers.forEach((resolve) => resolve(sessionData));
}

/**
 * Handle every integration.settings event for the lifetime of the page.
 * localStorage is only a warm cache; every live event remains authoritative.
 */
function persistentIntegrationSettingsHandler(sessionData: IntegrationSettingsResponse): void {
    Logger.info(
        `Integration settings event received '${sessionData?.data?.eventType}'`
    );
    if (sessionData?.data?.id) {
        openWebexUrlForAckEvent(sessionData.data.id, "Calling Dock");
    }
    eventEmitter.dispatch(EventType.INTEGRATION_SETTINGS_EVENT_TYPE, "integrationSettings");
    setLocalStorageItem(DISPATCH_STORAGE_EVENTS.STORAGE_INTEGRATION_SETTINGS_DATA, sessionData);
    dispatchScopedEvent("integrationSettings");
    flushPendingIntegrationSettingsResolvers(sessionData);
}

/**
 * Register the integration.settings Mercury listener exactly once per page.
 * This must run even when a warm settings cache exists, and before subscribing,
 * so restart snapshots and all later partial updates reach localStorage and UI.
 */
export function registerIntegrationSettingsListener(): void {
    if (!makeIntegrationSettingsRegister) {
        return;
    }

    const mercury = window?.webex?.internal?.mercury;
    if (!mercury || typeof mercury.on !== 'function') {
        Logger.info('Mercury not ready yet while registering integration settings listener');
        return;
    }

    mercury.on(
        DATA_PIPE_LINE_EVENTS.INTEGRATION_SETTINGS,
        persistentIntegrationSettingsHandler
    );
    makeIntegrationSettingsRegister = false;
    Logger.debug(
        `Mercury listener registered for event: ${DATA_PIPE_LINE_EVENTS.INTEGRATION_SETTINGS}`
    );
}

/** Test-only reset of integration settings listener state. */
export function __resetIntegrationSettingsListenerStateForTests(): void {
    makeIntegrationSettingsRegister = true;
    pendingIntegrationSettingsResolvers = [];
}

async function listenIntegrationSettingsStatus(
    existingData: IntegrationSettingsResponse | null =
        getLocalStorageItem<IntegrationSettingsResponse>(
            DISPATCH_STORAGE_EVENTS.STORAGE_INTEGRATION_SETTINGS_DATA
        )
): Promise<IntegrationSettingsResponse> {
    if (existingData?.data?.payload?.settings) {
        // Cache provides an immediate warm result, but never suppresses the live
        // listener. A subsequent full/partial Mercury event replaces this value.
        registerIntegrationSettingsListener();
        Logger.info(
            `Integration Settings data already in localStorage. Returning warm cached data while listening for updates.`
        );
        return existingData;
    }

    removeLocalStorageItem(DISPATCH_STORAGE_EVENTS.STORAGE_INTEGRATION_SETTINGS_DATA);
    return new Promise<IntegrationSettingsResponse>((resolve) => {
        pendingIntegrationSettingsResolvers.push(resolve);
        registerIntegrationSettingsListener();
    });
}

export async function getIntegrationSettingsData(attempt = 0): Promise<IntegrationSettingsResponse> {
    if (window?.isWebexInitalized) {
        // Create/register the listener before starting the subscription. The
        // desktop app can publish the full settings snapshot immediately.
        const existingData = getLocalStorageItem<IntegrationSettingsResponse>(
            DISPATCH_STORAGE_EVENTS.STORAGE_INTEGRATION_SETTINGS_DATA
        );
        const settingsPromise = listenIntegrationSettingsStatus(existingData);
        if (existingData?.data?.payload?.settings) {
            return settingsPromise;
        }
        // Capture the resolver that listenIntegrationSettingsStatus just pushed so
        // we can remove it if pipeline startup fails and prevent a stale resolver
        // from being triggered by a later settings event (state leak across retries).
        const pendingResolver = pendingIntegrationSettingsResolvers[pendingIntegrationSettingsResolvers.length - 1];
        try {
            await ensureDataPipelineStarted();
        } catch (err) {
            const idx = pendingIntegrationSettingsResolvers.indexOf(pendingResolver);
            if (idx !== -1) {
                pendingIntegrationSettingsResolvers.splice(idx, 1);
            }
            throw err;
        }
        return settingsPromise;
    } else if (attempt >= MAX_SDK_POLL_ATTEMPTS) {
        throw new Error('getIntegrationSettingsData: Webex SDK not initialized after max attempts');
    } else {
        await new Promise((f) => setTimeout(f, 1000));
        return getIntegrationSettingsData(attempt + 1);
    }
}

//Integration.E911 service

async function listenIntegrationE911Status(): Promise<IntegrationE911Response> {
    return new Promise<IntegrationE911Response>((resolve) => {
        // Check if data already exists before clearing
        const existingData = getLocalStorageItem<IntegrationE911Response>(
            DISPATCH_STORAGE_EVENTS.STORAGE_INTEGRATION_E911_DATA
        );
        if (existingData?.data?.payload?.emergencyLocationValidated !== undefined) {
            Logger.info(
                `[LineSelection] Integration E911 data already in localStorage. Returning cached data.`
            );
            resolve(existingData);
            return;
        }

        removeLocalStorageItem(DISPATCH_STORAGE_EVENTS.STORAGE_INTEGRATION_E911_DATA);
        if (makeIntegrationE911Register) {
            const eventHandler = (sessionData: IntegrationE911Response) => {
                Logger.info(
                    `User subscribed to the Integration e911 event '${sessionData.data.eventType}'`
                );
                eventEmitter.dispatch(EventType.INTEGRATION_E911_EVENT_TYPE, "integrationE911");
                //TO DO CHANGE MOCK TO REAL DATA
                setLocalStorageItem(DISPATCH_STORAGE_EVENTS.STORAGE_INTEGRATION_E911_DATA, sessionData);
                dispatchScopedEvent("integrationE911");
                resolve(sessionData); // Resolve the promise with the event payload
            };

            window.webex.internal.mercury.on(
                DATA_PIPE_LINE_EVENTS.INTEGRATION_E911,
                eventHandler
            );

            makeIntegrationE911Register = false;
        }
    });
}

export async function getIntegrationE911Data(attempt = 0): Promise<IntegrationE911Response> {
    const existingData = getLocalStorageItem<IntegrationE911Response>(
        DISPATCH_STORAGE_EVENTS.STORAGE_INTEGRATION_E911_DATA
    );
    if (existingData?.data?.payload?.emergencyLocationValidated !== undefined) {
        Logger.info('[LineSelection] getIntegrationE911Data: returning cached E911 data');
        return existingData;
    }

    if (window?.isWebexInitalized) {
        await ensureDataPipelineStarted();
        return listenIntegrationE911Status();
    } else if (attempt >= MAX_SDK_POLL_ATTEMPTS) {
        throw new Error('getIntegrationE911Data: Webex SDK not initialized after max attempts');
    } else {
        await new Promise((f) => setTimeout(f, 1000));
        return getIntegrationE911Data(attempt + 1);
    }
}

//error subscription event

async function listenErrorEventResponse(): Promise<IntegrationErrorResponse> {
    return new Promise<IntegrationErrorResponse>((resolve) => {
        removeLocalStorageItem(DISPATCH_STORAGE_EVENTS.STORAGE_INTEGRATION_ERROR);
        if (makeIntegrationErrorRegister) {
            const eventHandler = (sessionData: IntegrationErrorResponse) => {
                Logger.info(
                    `User recieved the error event '${sessionData.data.eventType}'`
                );
                eventEmitter.dispatch(EventType.INTEGRATION_ERROR_EVENT_TYPE, "integrationError");
                setLocalStorageItem(DISPATCH_STORAGE_EVENTS.STORAGE_INTEGRATION_ERROR, sessionData);
                dispatchScopedEvent("integrationError");
                resolve(sessionData); // Resolve the promise with the event payload
            };

            window.webex.internal.mercury.on(
                DATA_PIPE_LINE_EVENTS.INTEGRATION_ERROR,
                eventHandler
            );

            makeIntegrationErrorRegister = false;
        }
    });
}

export async function getErrorEventData(attempt = 0): Promise<IntegrationErrorResponse> {
    if (window?.isWebexInitalized) {
        return listenErrorEventResponse();
    } else if (attempt >= MAX_SDK_POLL_ATTEMPTS) {
        throw new Error('getErrorEventData: Webex SDK not initialized after max attempts');
    } else {
        await new Promise((f) => setTimeout(f, 1000));
        return getErrorEventData(attempt + 1);
    }
}
